import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const vodApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Attach auth token from localStorage on every request
vodApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('streamToken');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors globally
vodApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token
      localStorage.removeItem('streamToken');
      localStorage.removeItem('streamUser');
      // Redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

class VodService {
  // Media Directory Methods
  static async getAllDirectories() {
    try {
      const response = await vodApi.get('/vod/directories');
      return response.data;
    } catch (error) {
      console.error('Error fetching directories:', error);
      throw error;
    }
  }

  static async createDirectory(directoryData) {
    try {
      const response = await vodApi.post('/vod/directories', directoryData);
      return response.data;
    } catch (error) {
      console.error('Error creating directory:', error);
      throw error;
    }
  }

  static async updateDirectory(id, updates) {
    try {
      const response = await vodApi.put(`/vod/directories/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating directory:', error);
      throw error;
    }
  }

  static async deleteDirectory(id) {
    try {
      const response = await vodApi.delete(`/vod/directories/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting directory:', error);
      throw error;
    }
  }

  // VOD Assets Methods
  static async getAllAssets() {
    try {
      const response = await vodApi.get('/vod/assets');
      return response.data;
    } catch (error) {
      console.error('Error fetching VOD assets:', error);
      throw error;
    }
  }

  static async getAssetsByDirectory(directoryId) {
    try {
      const response = await vodApi.get(`/vod/assets?directory_id=${directoryId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching directory assets:', error);
      throw error;
    }
  }

  static async uploadAsset(formData, onProgress) {
    try {
      const response = await vodApi.post('/vod/assets/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: onProgress
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading asset:', error);
      throw error;
    }
  }

  static async updateAsset(id, updates) {
    try {
      const response = await vodApi.put(`/vod/assets/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating VOD asset:', error);
      throw error;
    }
  }

  static async deleteAsset(id) {
    try {
      const response = await vodApi.delete(`/vod/assets/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting asset:', error);
      throw error;
    }
  }

  // Playlist Methods
  static async getAllPlaylists() {
    try {
      const response = await vodApi.get('/vod/playlists');
      return response.data;
    } catch (error) {
      console.error('Error fetching playlists:', error);
      throw error;
    }
  }

  static async getPlaylist(id) {
    try {
      const response = await vodApi.get(`/vod/playlists/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching playlist:', error);
      throw error;
    }
  }

  static async createPlaylist(playlistData) {
    try {
      const response = await vodApi.post('/vod/playlists', playlistData);
      return response.data;
    } catch (error) {
      console.error('Error creating playlist:', error);
      throw error;
    }
  }

  static async updatePlaylist(id, updates) {
    try {
      const response = await vodApi.put(`/vod/playlists/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating playlist:', error);
      throw error;
    }
  }

  static async deletePlaylist(id) {
    try {
      const response = await vodApi.delete(`/vod/playlists/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting playlist:', error);
      throw error;
    }
  }

  // Schedule Methods
  static async getAllSchedules() {
    try {
      const response = await vodApi.get('/vod/schedules');
      return response.data;
    } catch (error) {
      console.error('Error fetching schedules:', error);
      throw error;
    }
  }

  static async createSchedule(scheduleData) {
    try {
      const response = await vodApi.post('/vod/schedules', scheduleData);
      return response.data;
    } catch (error) {
      console.error('Error creating schedule:', error);
      throw error;
    }
  }

  static async updateSchedule(id, updates) {
    try {
      const response = await vodApi.put(`/vod/schedules/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating schedule:', error);
      throw error;
    }
  }

  static async deleteSchedule(id) {
    try {
      const response = await vodApi.delete(`/vod/schedules/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting schedule:', error);
      throw error;
    }
  }

  // Automation Control
  static async toggleAutomation(scheduleId, isActive) {
    try {
      const response = await vodApi.put(`/vod/schedules/${scheduleId}`, {
        is_active: isActive
      });
      return response.data;
    } catch (error) {
      console.error('Error toggling automation:', error);
      throw error;
    }
  }

  // Get all streams for schedule assignment
  static async getAllStreams() {
    try {
      const response = await vodApi.get('/streams');
      return response.data;
    } catch (error) {
      console.error('Error fetching streams:', error);
      throw error;
    }
  }

  // Video Editing Methods
  static async editVideo(assetId, editData) {
    try {
      const response = await vodApi.post(`/vod/assets/${assetId}/edit`, editData);
      return response.data;
    } catch (error) {
      console.error('Error editing video:', error);
      throw error;
    }
  }

  static async downloadVideo(assetId) {
    try {
      const response = await vodApi.get(`/vod/assets/${assetId}/download`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading video:', error);
      throw error;
    }
  }

  static async getVideoEditStatus(assetId) {
    try {
      const response = await vodApi.get(`/vod/assets/${assetId}/edit-status`);
      return response.data;
    } catch (error) {
      console.error('Error getting edit status:', error);
      throw error;
    }
  }

  // Caption Management Methods

  // Get caption tracks for a VOD asset
  static async getCaptions(assetId) {
    try {
      const response = await vodApi.get(`/vod/assets/${assetId}/captions`);
      return response;
    } catch (error) {
      console.error('Error fetching captions:', error);
      throw error;
    }
  }

  // Add caption track to VOD asset
  static async addCaption(assetId, captionData) {
    try {
      const response = await vodApi.post(`/vod/assets/${assetId}/captions`, captionData);
      return response;
    } catch (error) {
      console.error('Error adding caption:', error);
      throw error;
    }
  }

  // Remove caption track from VOD asset
  static async removeCaption(assetId, language) {
    try {
      const response = await vodApi.delete(`/vod/assets/${assetId}/captions/${language}`);
      return response;
    } catch (error) {
      console.error('Error removing caption:', error);
      throw error;
    }
  }

  // Set default caption language for VOD asset
  static async setDefaultCaption(assetId, language) {
    try {
      const response = await vodApi.put(`/vod/assets/${assetId}/captions/default`, { language });
      return response;
    } catch (error) {
      console.error('Error setting default caption:', error);
      throw error;
    }
  }

  // Direct, token-authenticated URLs for <img>/<video> tags
  static getAssetThumbnailUrl(assetId) {
    const token = localStorage.getItem('streamToken') || '';
    return `${API_URL}/vod/assets/${assetId}/thumbnail?token=${encodeURIComponent(token)}`;
  }

  static getAssetStreamUrl(assetId) {
    const token = localStorage.getItem('streamToken') || '';
    return `${API_URL}/vod/stream/${assetId}?token=${encodeURIComponent(token)}`;
  }

  // Account-wide auto-failover toggle
  static async getFailoverSettings() {
    try {
      const response = await vodApi.get('/vod/failover/settings');
      return response.data;
    } catch (error) {
      console.error('Error fetching auto-failover setting:', error);
      throw error;
    }
  }

  static async setFailoverSettings(enabled) {
    try {
      const response = await vodApi.put('/vod/failover/settings', { enabled });
      return response.data;
    } catch (error) {
      console.error('Error updating auto-failover setting:', error);
      throw error;
    }
  }
}

export default VodService;
